<script setup>
import { ref } from "vue";
import ButtonCta from "@components/ButtonCta.vue";
import { useSmoothScroll } from "@composables/useSmoothScroll";

const navLinks = [
  { label: "What is it", href: "#hero" },
  { label: "Why Apply", href: "#why" },
  { label: "Follow Us", href: "#follow-us" },
];

const menuOpen = ref(false);
const { smoothScroll, handleAnchorClick } = useSmoothScroll();

function handleMobileNavClick(e, href) {
  menuOpen.value = false;
  handleAnchorClick(e, href);
}
</script>

<template>
  <header class="fixed top-4 left-0 right-0 z-50 flex justify-center px-4">
    <nav
      class="flex w-full max-w-[1408px] flex-col rounded-[20px] bg-black p-[16px] md:flex-row md:items-center md:justify-between"
    >
      <!-- Top row: Logo + Menu button -->
      <div
        class="flex w-full items-center justify-between md:w-auto md:justify-start"
      >
        <!-- Logo -->
        <a
          href="/"
          class="shrink-0 pb-[12px]"
          @click="smoothScroll($event, '#hero')"
        >
          <img
            src="https://wishlisted.s3.amazonaws.com/images/logos/logo.svg"
            alt="Wishlisted"
            class="h-[40px] w-[177px]"
          />
        </a>

        <!-- Mobile menu button -->
        <ButtonCta
          square
          class="!h-[56px] !w-[56px] md:hidden"
          :aria-expanded="menuOpen"
          aria-label="Toggle menu"
          @click="menuOpen = !menuOpen"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <template v-if="!menuOpen">
              <rect
                x="3"
                y="5"
                width="18"
                height="2.5"
                rx="1.25"
                fill="black"
              />
              <rect
                x="3"
                y="11"
                width="18"
                height="2.5"
                rx="1.25"
                fill="black"
              />
              <rect
                x="3"
                y="17"
                width="18"
                height="2.5"
                rx="1.25"
                fill="black"
              />
            </template>
            <template v-else>
              <rect
                x="4.93"
                y="3.87"
                width="21"
                height="2.5"
                rx="1.25"
                transform="rotate(45 4.93 3.87)"
                fill="black"
              />
              <rect
                x="3.16"
                y="18.72"
                width="21"
                height="2.5"
                rx="1.25"
                transform="rotate(-45 3.16 18.72)"
                fill="black"
              />
            </template>
          </svg>
        </ButtonCta>
      </div>

      <!-- Desktop nav links -->
      <ul
        class="hidden items-center gap-[40px] font-obviously font-bold text-[14px] tracking-[-0.14px] text-white md:flex"
      >
        <li v-for="link in navLinks" :key="link.href">
          <a
            :href="link.href"
            class="group relative block h-[24px] overflow-hidden leading-[24px]"
            @click="handleAnchorClick($event, link.href)"
          >
            <span
              class="block transition-transform duration-300 ease-out group-hover:-translate-y-full"
            >
              {{ link.label }}
            </span>
            <span
              class="block transition-transform duration-300 ease-out group-hover:-translate-y-full"
              aria-hidden="true"
            >
              {{ link.label }}
            </span>
          </a>
        </li>
      </ul>

      <!-- Desktop CTA -->
      <ButtonCta
        label="Apply now"
        variant="primary"
        href="https://forms.gle/5Sf4He7CEVWMhYcCA"
        class="hidden md:flex"
      />

      <!-- Mobile menu (animated expand/collapse) -->
      <div
        class="mobile-menu-grid grid md:hidden"
        :class="menuOpen ? 'mobile-menu-open' : ''"
      >
        <div class="overflow-hidden">
          <div class="flex flex-col gap-[56px] pt-[56px] pb-[8px]">
            <!-- Mobile nav links -->
            <ul
              class="flex w-full flex-col items-center gap-[24px] font-obviously font-bold text-[14px] tracking-[-0.14px] text-white"
            >
              <li
                v-for="(link, i) in navLinks"
                :key="link.href"
                class="mobile-nav-item"
                :class="menuOpen ? 'mobile-nav-item-visible' : ''"
                :style="{
                  transitionDelay: menuOpen ? `${150 + i * 60}ms` : '0ms',
                }"
              >
                <a
                  :href="link.href"
                  class="block leading-[24px]"
                  @click="handleMobileNavClick($event, link.href)"
                >
                  {{ link.label }}
                </a>
              </li>
            </ul>

            <!-- Mobile CTA -->
            <ButtonCta
              label="Apply now"
              variant="primary"
              href="https://forms.gle/5Sf4He7CEVWMhYcCA"
              class="mobile-nav-item w-full"
              :class="menuOpen ? 'mobile-nav-item-visible' : ''"
              :style="{
                transitionDelay: menuOpen
                  ? `${150 + navLinks.length * 60}ms`
                  : '0ms',
              }"
            />
          </div>
        </div>
      </div>
    </nav>
  </header>
</template>

<style scoped>
.mobile-menu-grid {
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.4s var(--ease-out-expo);
}

.mobile-menu-open {
  grid-template-rows: 1fr;
}

.mobile-nav-item {
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.mobile-nav-item-visible {
  opacity: 1;
  transform: translateY(0);
}
</style>
